import React, { useMemo } from "react";
import { Box, IconButton, useTheme } from "@mui/material";
import { Link, useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
// Same iconsax set LeftNav.jsx's own rail already uses (the app-wide standard,
// see icon-library-iconsax memory) -- this file was never migrated when that
// happened, so mobile showed the old plain-MUI icon set while desktop showed
// iconsax, reading as "two different navbars" (Rahim, 2026-09-10). Logout
// stays MUI to match LeftNav's own logout menu item, which is MUI too.
import {
  Category2 as DashboardIconIx,
  DirectInbox as InboxIconIx,
  Profile2User as ContactsIconIx,
  TaskSquare as TaskIconIx,
  Hierarchy as WorkflowIconIx,
  DocumentText as TemplatesIconIx,
  Chart as ReportsIconIx,
  Setting2 as SettingsIconIx,
  Send2 as OnboardIconIx,
} from "iconsax-react";
import AiFaceIcon from "./AiFaceIcon";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  setSelectedIndex,
  setCurrentPage,
  setBreadcrumbs,
} from "../../redux/Slice/Dashboard/leftNavSlice";
import { logoutUser } from "../../redux/Slice/Auth/authSlice";

const BottomNav = React.memo(() => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const location = useLocation();
  const { accountid, userid, templateId } = useParams();
  const { userDetails, userPermissions } = useSelector((state) => state.userDetails);
  const subscriptionFeatures = useSelector((state) => state.subscription?.features);

  const aiPermissionAllowed =
    userPermissions == null ||
    userPermissions?.permissions?.AI?.View === true ||
    userPermissions?.permissions?.AI?.Edit === true;
  // Fail-open while features haven't loaded yet, same as the permissions check above.
  const aiFeatureEnabled =
    subscriptionFeatures == null ||
    subscriptionFeatures.length === 0 ||
    subscriptionFeatures.find((f) => f.key === "aiAssistants")?.enabled !== false;
  const aiNavAllowed = aiPermissionAllowed && aiFeatureEnabled;

  // Organize menu items (same as LeftNav but flattened)
  const menuItems = useMemo(
    () => [
      { text: "Onboard", Icon: OnboardIconIx, url: `/home/${accountid}/onboard` },
      { text: "Dashboard", Icon: DashboardIconIx, url: `/home/${accountid}` },
      {
        text: "Inbox",
        Icon: InboxIconIx,
        url: `/home/${accountid}/inbox/${
          userDetails ? userDetails._id : userid
        }`,
        matches: (path) => path.startsWith(`/home/${accountid}/inbox/`),
      },
      {
        text: "Contacts",
        Icon: ContactsIconIx,
        url: `/home/${accountid}/contacts`,
      },
      {
        text: "Tasks",
        Icon: TaskIconIx,
        url: `/home/${accountid}/tasks`,
      },
      {
        text: "Workflow",
        Icon: WorkflowIconIx,
        url: `/home/${accountid}/workflow`,
      },
      {
        text: "Templates",
        Icon: TemplatesIconIx,
        url: `/home/${accountid}/templates`,
        matches: (path) =>
          path === `/home/${accountid}/templates` ||
          path === `/home/${accountid}/templates/create` ||
          path === `/home/${accountid}/templates/analytics/${templateId}`,
      },
      {
        text: "Reports",
        Icon: ReportsIconIx,
        url: `/home/${accountid}/reports`,
      },
      ...(aiNavAllowed
        ? [
            {
              text: "AI Agents",
              Icon: AiFaceIcon,
              url: `/home/${accountid}/ai`,
              matches: (path) => path === `/home/${accountid}/ai`,
            },
          ]
        : []),
      {
        text: "Settings",
        Icon: SettingsIconIx,
        url: `/home/${accountid}/settings`,
      },
      {
        text: "Logout",
        Icon: LogoutIcon,
        url: null, // Logout doesn't have a URL
      },
    ],
    [accountid, userDetails, userid, templateId, aiNavAllowed]
  );

  // Find current active index
  const currentIndex = useMemo(() => {
    return menuItems.findIndex(
      (item) =>
        item.url === location.pathname ||
        (item.matches && item.matches(location.pathname))
    );
  }, [location.pathname, menuItems]);

  const handleItemClick = (index, item) => {
    dispatch(setSelectedIndex(index));

    // Handle logout separately
    if (item.text === "Logout") {
      dispatch(logoutUser());
      return;
    }

    // Update current page in redux
    dispatch(setCurrentPage(item.text));

    // Handle breadcrumbs for template pages
    if (item.text === "Templates" && location.pathname.includes("/create")) {
      dispatch(setBreadcrumbs(["Templates", "Create"]));
    } else {
      dispatch(setBreadcrumbs([item.text]));
    }
  };

  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        // xs/sm: in document flow under main outlet (Home three-row flex). md+: hidden.
        position: "relative",
        width: "100%",
        flexShrink: 0,
        minHeight: { xs: "var(--mobile-bottom-nav-height, 56px)", sm: "var(--mobile-bottom-nav-height, 62px)" },
        boxSizing: "border-box",
        display: { xs: "flex", sm: "flex", md: "none", lg: "none", xl: "none" },
        justifyContent: { xs: "flex-start", sm: "center", md: "flex-start" },
        alignItems: "center",
        backgroundColor: isDark
          ? theme.customColors?.dark?.navigation || "#23272b"
          : theme.palette.background.default,
        borderTop: `1px solid ${isDark ? "#333": "#e0e0e0"}`, 
        padding: { xs: "8px 4px", sm: "8px 0" },
        paddingBottom: { xs: "max(8px, env(safe-area-inset-bottom, 0px))", sm: "max(8px, env(safe-area-inset-bottom, 0px))", md: 0, lg: 0, xl: 0 },
        zIndex: { xs: 1, sm: 1, md: 1000, lg: 1000, xl: 1000 },
        overflowX: { xs: "auto", sm: "hidden" },
        overflowY: "hidden",
        // Hide scrollbar but keep scrolling functionality on xs
        scrollbarWidth: { xs: "none", sm: "auto" }, // Firefox
        msOverflowStyle: { xs: "none", sm: "auto" }, // IE and Edge (old)
        "&::-webkit-scrollbar": {
          height: { xs: 0, sm: "4px" }, // Hide scrollbar on xs by setting height to 0
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isDark ? "#555" : "#ccc",
          borderRadius: "2px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, sm: 2 },
          paddingX: { xs: 1, sm: 0 },
          minWidth: { xs: "max-content", sm: "auto" },
          width: { xs: "auto", sm: "auto" },
        }}
      >
        {menuItems.map((item, index) => {
          const isActive = currentIndex === index;
          const IconComponent = item.Icon;

          if (item.text === "Logout") {
            return (
              <IconButton
                key={item.text}
                onClick={() => handleItemClick(index, item)}
                sx={{
                  color: isActive
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                  backgroundColor: isActive
                    ? theme.palette.primary.main
                    : "transparent",
                  borderRadius: "12px",
                  padding: { xs: "8px", sm: "10px" },
                  minWidth: { xs: "48px", sm: "56px" },
                  "&:hover": {
                    backgroundColor: isActive
                      ? theme.palette.primary.main
                      : theme.palette.action.hover,
                  },
                  "& .MuiSvgIcon-root": {
                    color: isActive
                      ? "#fff"
                      : theme.palette.text.secondary,
                  },
                }}
              >
                <IconComponent sx={{ fontSize: { xs: 24, sm: 26 } }} />
              </IconButton>
            );
          }

          return (
            <Link
              key={item.text}
              to={item.url}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <IconButton
                onClick={() => handleItemClick(index, item)}
                sx={{
                  backgroundColor: isActive
                    ? theme.palette.primary.main
                    : "transparent",
                  borderRadius: "12px",
                  padding: { xs: "8px", sm: "10px" },
                  minWidth: { xs: "48px", sm: "56px" },
                  "&:hover": {
                    backgroundColor: isActive
                      ? theme.palette.primary.main
                      : theme.palette.action.hover,
                  },
                }}
              >
                {/* Iconsax icons (everything but Logout, see the import comment
                    above) don't accept MUI's `sx` prop or pick up the old
                    ".MuiSvgIcon-root" color override -- same Box+size="100%"
                    wrapper and explicit color LeftNav.jsx's own rail already
                    uses, so active/inactive reads identically to desktop. */}
                <Box
                  sx={{
                    width: { xs: 24, sm: 26 },
                    height: { xs: 24, sm: 26 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconComponent
                    size="100%"
                    color={isActive ? "#fff" : theme.palette.text.secondary}
                    variant={isActive ? "Bold" : "Linear"}
                  />
                </Box>
              </IconButton>
            </Link>
          );
        })}
      </Box>
    </Box>
  );
});

BottomNav.displayName = "BottomNav";

export default BottomNav;

